import { useRef, useState } from 'react';
import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { personName } from '@/i18n/personName';
import { virtualAccountsApi, type VirtualAccountDetail } from '@/api/virtualAccounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import type { MeVirtualAccount } from '@/api/me';
import { envelopesApi, type Envelope } from '@/api/envelopes';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DialogSubtitle } from '@/components/DialogSubtitle';

interface MoveEnvelopeDialogProps {
  envelope: Envelope;
  /** The virtual account the envelope sits in today. */
  current: VirtualAccountDetail;
  onClose: () => void;
  /** The move succeeded: the target and the SERVER's audience after the move. */
  onMoved: (target: MeVirtualAccount, audienceAfter: string[]) => void | Promise<void>;
}

type MoveError = 'forbidden' | 'generic' | null;

/** ACTIVE share names: display name, then served email, then "A member" (0097 §E 34). */
function audienceOf(detail: VirtualAccountDetail, t: TFunction): string[] {
  return detail.shares
    .filter((share) => share.status === 'ACTIVE')
    .map((share) => share.displayName ?? share.email ?? share.boundEmail ?? personName(null, t));
}

/**
 * Move an envelope to another owned ACTIVE virtual account (design §5.1 — an envelope's audience is its virtual account's).
 * A confirmation (`ConfirmDialog`, TMM-B4): picking the target is the confirmation's parameter —
 * the pick fetches its detail and states who gains and who loses sight of the envelope before the owner
 * confirms. Nothing is typed, so there is no discard question (a pick is one click to redo).
 */
export function MoveEnvelopeDialog({
  envelope,
  current,
  onClose,
  onMoved,
}: MoveEnvelopeDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const [targetId, setTargetId] = useState<number | null>(null);
  const [target, setTarget] = useState<VirtualAccountDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<MoveError>(null);
  const [subscribeRequired, setSubscribeRequired] = useState(false);
  // A later pick supersedes an in-flight detail fetch.
  const pick = useRef(0);

  const options = me.virtualAccounts.filter((d) => d.mine && d.id !== current.id);
  const list = new Intl.ListFormat(locale, { type: 'conjunction' });

  async function handlePick(id: number) {
    const token = ++pick.current;
    setTargetId(id);
    setTarget(null);
    setError(null);
    setLoading(true);
    try {
      const detail = await virtualAccountsApi.get(id);
      if (token === pick.current) setTarget(detail);
    } catch {
      if (token === pick.current) setError('generic');
    } finally {
      if (token === pick.current) setLoading(false);
    }
  }

  async function handleConfirm() {
    if (targetId === null || target === null || submitting) return;
    const chosen = options.find((d) => d.id === targetId);
    if (!chosen) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await envelopesApi.move(envelope.id, targetId);
      await onMoved(chosen, result.audienceAfter);
      onClose();
    } catch (caught) {
      const code = isApiError(caught) ? caught.code : ErrorCodes.UNKNOWN;
      if (code === ErrorCodes.FORBIDDEN) setError('forbidden');
      else if (code === ErrorCodes.SUBSCRIPTION_REQUIRED) setSubscribeRequired(true);
      else setError('generic');
      setSubmitting(false);
    }
  }

  const before = audienceOf(current, t);
  const after = target ? audienceOf(target, t) : [];
  const loses = before.filter((name) => !after.includes(name));
  const gains = after.filter((name) => !before.includes(name));

  return (
    <ConfirmDialog
      title={t('envelope.moveTitle', { name: envelope.name })}
      subtitle={
        <DialogSubtitle>{t('envelope.moveSubtitle', { from: current.name })}</DialogSubtitle>
      }
      consequence={
        target
          ? t('envelope.moveConfirm', { name: envelope.name, virtualAccount: target.name })
          : t('envelope.movePick')
      }
      confirmLabel={t('envelope.moveAction')}
      cancelLabel={subscribeRequired ? t('common.close') : undefined}
      busy={submitting}
      confirmDisabled={target === null}
      hideConfirm={subscribeRequired}
      error={
        subscribeRequired || error === null
          ? undefined
          : error === 'forbidden'
            ? t('virtualAccounts.errorForbidden')
            : t('virtualAccounts.errorGeneric')
      }
      onConfirm={() => void handleConfirm()}
      onCancel={onClose}
    >
      {subscribeRequired ? (
        <SubscribeInPlace onClose={onClose} />
      ) : (
        <Stack spacing={2}>
          <FormControl>
            <FormLabel id="move-envelope-target">{t('nav.virtualAccounts')}</FormLabel>
            <RadioGroup
              aria-labelledby="move-envelope-target"
              value={targetId ?? ''}
              onChange={(event) => void handlePick(Number(event.target.value))}
            >
              {options.map((d) => (
                <FormControlLabel
                  key={d.id}
                  value={d.id}
                  control={<Radio />}
                  label={d.name}
                  disabled={submitting}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {loading && <CircularProgress size={20} aria-label={t('common.loading')} />}
          {target && (
            <Stack spacing={0.5}>
              {loses.length > 0 && (
                <Typography variant="body2">
                  {t('envelope.moveLoses', { people: list.format(loses) })}
                </Typography>
              )}
              {gains.length > 0 && (
                <Typography variant="body2">
                  {t('envelope.moveGains', { people: list.format(gains) })}
                </Typography>
              )}
              {after.length === 0 && (
                <Typography variant="body2">{t('envelope.movePrivate')}</Typography>
              )}
            </Stack>
          )}
        </Stack>
      )}
    </ConfirmDialog>
  );
}
